// @flow
import LIUtils from "./LIUtils";
import Profile from "./Profile";
import {renderToString} from "react-dom/server";

class GuiUtils {
    
    /**
     * build a pretty location based on what's available in the location, department, and contact objects
     * 
     * @param location
     * @param department
     * @param contact
     * @returns {string}
     * @constructor
     */
    static BuildPrettyLocation(location:Object, department:Object, contact:Object):string {
        const c = contact?.fullName ?? "";
        const d = department?.name ?? "";
        const l = location?.name ?? "";
        let output = "";

        // ### this could be better
        if (l && d && c) { // location, contact, and department
            output = c + "@" + l + "/" + d;
        } else if (l && c) { // location and contact
            output = c + "@" + l;
        } else if (l) { // location only
            output = l;
        } else if (c) {  // contact only
            output = c;
        }
        
        return output;
    }

    /**
     * given sample basic info, build a string that includes at least the basic identification info, handling the
     * case were the barcode may be missing and/or the case/sample may be missing.
     * @param barcode
     * @param caseNumber
     * @param sampleNumber
     * @returns {string}
     * @constructor
     */
    static BuildSampleDetailsStr(barcode:string, caseNumber:string, sampleNumber:string) {
        if(caseNumber && caseNumber!=="" && sampleNumber && sampleNumber!=="") {
            return caseNumber + (LIUtils.existsAndNotEmpty(sampleNumber) ? ("-" + sampleNumber) : "");
        }
        if (barcode && barcode !== "") {
            return LIUtils.SanitizeBarcodeToLength(barcode);
        }
        return("unknown");
    }

    /**
     * given sample basic info, build a string that includes at least the basic identification info, that
     * concentrates on the case and sample numbers, not the barcode at all.
     * @param caseNumber
     * @param sampleNumber
     * @returns {string}
     * @constructor
     */
    static BuildCaseSampleDetailsStr(caseNumber:string, sampleNumber:string) {
        let caseSample="-";
        if(caseNumber && caseNumber!=="") {
            caseSample=caseNumber+(LIUtils.existsAndNotEmpty(sampleNumber)?("-"+sampleNumber):"");
        }
        return caseSample;
    }
    
    /** return s string value of the given number that is padded with leading zeros to the given width
     * @param value
     * @param width
     * @returns {string}
     */
    static prettyNumberWithWidth(value:number, width:number):string {
        return value.toString().padStart(width,"0");
    };

    /**
     * return a simple string that describes why a sample may not have been found
     * @returns {string}
     * @constructor
     */
    static GetSampleNotFoundMessage():string {
        return "<p><strong>Your sample was not found.</strong><p/>" +
            "<p>This sample will not be available any longer if you have<br/> " +
            "just repaired a sample and it was found to be a duplicate.<p/>" +
            "<p class='font-weight-bold text-center'>The original sample will still be available.</p>" +
            "<p>Try searching for it again.</p>";
    }

    /** convert the given number to its equivalent 1 or 2 digit alpha character value
     * 
     * @param num
     * @returns {*}
     */
    static numberToAlpha(num:number):string {
        const alpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        if (num<1 || num>600) { // out of range (26*26) minus a bit
            return "-";
        }

        const multiplier = Math.floor((num-1)/26);
        const remainder = num - 26*multiplier;
        const firstPart = multiplier>0 ? alpha[multiplier-1] : "";
        const secondPart = alpha[remainder-1];
        return (firstPart+secondPart);
    }

    /** helper to get the name of a sample type given its id */
    static GetSampleTypeNameGivenId(id, sampleTypes) {
        const sampleType = sampleTypes.find((item) => item.id === Number(id));
        return sampleType ? sampleType.name : "Unknown";
    }

    /**
     * helper to get the name of a mag type given its id
     * @param id
     * @param magTypes
     * @returns {*|string}
     * @constructor
     */
    static GetMagTypeNameGivenId(id, magTypes) {
        const magType = magTypes.find((item) => item.id === Number(id));
        return magType ? magType.description : "Unknown";
    }

    /** helper to get the image for a given sample type given its id
     * This returns null if no image was found */
    static GetSampleTypeImageGivenId(id, sampleTypes) {
        const sampleType = sampleTypes.find((item) => item.id === Number(id));
        return sampleType ? sampleType.imageData : null;
    }

    /** get the height of some rendered text within a canvas context */
    static GetRenderedTextHeight(ctx:CanvasRenderingContext2D, text:string):number {

        // we use the font in the context currently
        //ctx.font = font;
        const metrics = ctx.measureText(text);
        return metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    }

    /** get the width of some rendered text within a canvas context */
    static GetRenderedTextWidth(ctx:CanvasRenderingContext2D, text:string): number {

        // we use the font in the context currently
        //ctx.font = font;
        const metrics = ctx.measureText(text);
        return metrics.width;
    }

    /** build a pretty list of sample profile names for the profiles that have been passed in
     *
     * @param sampleProfiles
     * @param includeValidations include the validation strings for the main PPRs
     * @param includePPRName include the name of the main PPR
     * @param includeCaseAndSampleHints include any case and sample hints that may be available
     * @returns {string}
     */
    static prettySampleProfileListAsHtml(sampleProfiles:Profile[], includeValidations:boolean=false,
                                         includePPRName:boolean=false,includeCaseAndSampleHints:boolean=false): string {
        if (!sampleProfiles || sampleProfiles.length === 0) {
            return "<p>No profiles found</p>";
        }
        const formattedSampleList = sampleProfiles.map((profile:Profile,index) => {
            let retVal = null;
            if (includePPRName) {
                retVal = <> {retVal} =&gt; <i className="li-fg-admin">{profile.GetBarcodeProcessingRule()?.name??"no name"}</i></>;
            }
            if (includeValidations) {
                retVal = <> {retVal} =&gt; Barcode Matching: <i className="li-fg-success">{profile.GetValidationsAsString()}</i></>;
            }
            if (includeCaseAndSampleHints) {
                retVal = <> {retVal} =&gt; ex. <i>{profile.GetHintsAsString()}</i></>;
            }
            return <><span className="font-weight-bold li-fg-primary">{profile.name}</span> {retVal} </>;
        })

        const html = renderToString(<div>
        <p>Our current SAMPLE profiles:</p>
            <ul>
                {formattedSampleList}
            </ul>
        </div>);
        return html;
    }

    static prettySampleProfileListAsTable(sampleProfiles:Profile[]): string {
        if (!sampleProfiles || sampleProfiles.length === 0) {
            return "<p>No profiles found</p>";
        }
        const formattedSampleList = sampleProfiles.map((profile:Profile,index) => {
            return <tr key={index} className={profile.type??-1===0?"li-bg-white":"li-bg-light"}>
                <td>{profile.name}</td>
                <td>{profile.GetBarcodeProcessingRule()?.name??"no name"}</td>
                <td>{profile.GetValidationsAsString()}</td>
                <td>{profile.GetHintsAsString()}</td>
            </tr>;
        })

        const html = renderToString(
            <div>
                        <p>Current profiles (magazines are highlighted in grey):</p>
                        <table className="table table-bordered table-sm">
                            <thead className="li-bg-primary li-fg-white">
                                <tr><th>Name</th><th>Description</th><th>Matches</th><th>Case/Sample Hints</th></tr>
                            </thead>
                            <tbody>
                                {formattedSampleList}
                            </tbody>
                        </table>
                    </div>);
        return html;
    }

    /** build a pretty list of mag/rack profile names for the profiles that have been passed in
     *
     * @param magProfiles
     * @param includeValidations
     * @returns {string}
     */
    static prettyMagProfileListAsHtml(magProfiles:Profile[], includeValidations:boolean=false): string {
        if (!magProfiles || magProfiles.length === 0) {
            return "<p>No profiles found</p>";
        }
        const formattedMagList = magProfiles.map((profile:Profile, index:number) => {
            if (includeValidations) {
                return <li key={index}><span className="font-weight-bold li-fg-primary">{profile.name}</span> =&gt; Matching: <i>{profile.GetValidationsAsString()}</i></li>;
            }
            return <li>{profile.name}</li>;
        });
        
        const html = renderToString( <div>
            <p>Our current MAGAZINE/RACK profiles:</p>
            <ul>
                {formattedMagList}
            </ul>
        </div>);
        return html;
    }
    
    static prettyMagProfileListAsTable(magProfiles:Profile[]): string {
        if (!magProfiles || magProfiles.length === 0) {
            return "<p>No profiles found</p>";
        }
        const formattedMagList = magProfiles.map((profile:Profile, index:number) => {
            return <tr key={index}>
                <td>{profile.name}</td>
                <td>{profile.GetBarcodeProcessingRule()?.name??"no name"}</td>
                <td>{profile.GetValidationsAsString()}</td>
            </tr>;
        })

        const html = renderToString(
            <div>
                <p>Our current MAGAZINE/RACK profiles:</p>
                <table className="table table-bordered table-sm">
                    <thead className="li-bg-primary li-fg-white">
                        <tr><th>Name</th><th>Description</th><th>Matches</th></tr>
                    </thead>
                    <tbody>
                        {formattedMagList}
                    </tbody>
                </table>
            </div>);
        return html;
    }
}

export default GuiUtils;